<script lang="ts" setup>
import { WidgetContext } from "@/shared/environment/widgets/widget-context";
import { CommonRouteAliases } from "@/shared/environment/common-route-aliases";
import QitRouterLink from "@/shared/components/router-link/qit-router-link.vue";
import { useAbilityRoute } from "@/shared/environment/ability-route";
import { useActivityRoute } from "@/shared/router/activity-route";
import ListCard from "@/shared/components/card/list-card.vue";
import { useScreenSize } from "@/shared/screen/composables/screen-size.ts";
import PropertyView from "@/shared/components/views/property-view.vue";
import { useActivitiesSearch } from "@/abilities/activities/composeables/activities-search.ts";
import { computed } from "vue";
import { getSearchContextByWidgetContext } from "@/shared/utils/widget-utils.ts";
import WidgetHeader from "@/shared/components/widgets/widget-header.vue";
import { RouteElementWithParam } from "@/shared/environment/ability.types.ts";

const props = defineProps<{
    widgetContext: WidgetContext;
}>();

const { calculatePathForActivitiesSubalias } = useActivityRoute();
const { getPathToChildrenWithoutParents, getRouteElementsBySearchContext } = useAbilityRoute();
const { isLessThanMedium } = useScreenSize();

const searchContext = computed(() => {
    return getSearchContextByWidgetContext(props.widgetContext);
});

const { loading, activities } = useActivitiesSearch({
    searchContext,
    showFirstRelevantPropertyItemOnly: true,
    count: 6,
});

const isWidgetVisible = computed(() => {
    return !loading.value && activities.value && activities.value.length > 0;
});

const getPathToActivities = () => {
    const routeElements: RouteElementWithParam[] = getRouteElementsBySearchContext(searchContext.value);

    routeElements.push({
        alias: CommonRouteAliases.activities,
        namedParams: {},
    });

    return getPathToChildrenWithoutParents(routeElements);
};

const getPath = (activityId: string) => {
    return calculatePathForActivitiesSubalias(CommonRouteAliases.activityOverview, activityId, searchContext.value);
};
</script>

<template>
    <div v-if="isWidgetVisible" id="related-activities">
        <WidgetHeader :title="$t('activities.Activities')" icon="fa-regular fa-wrench" class="q-mb-md">
            <template #right>
                <QitRouterLink
                    data-qs="related-activities-view-all-activities-btn"
                    :to="getPathToActivities()"
                    :label="$t('shared.View all activities')"
                    hover-background
                />
            </template>
        </WidgetHeader>
        <div data-qs="related-activities-widget">
            <div class="row" :class="{ 'q-col-gutter-md': !isLessThanMedium, gutter: isLessThanMedium }">
                <div class="col-md-4 col-xs-12" v-for="(activity, index) in activities" :key="activity.id">
                    <router-link :to="getPath(activity.id)">
                        <ListCard
                            card-type="small"
                            class="activity-card"
                            :title="activity.title"
                            :locale-tag="activity.language"
                            :test-id="`related-activities-${index}`"
                            fallback-icon="fa-wrench"
                        >
                            <template #content>
                                <div v-if="activity.propertyItems" class="row properties">
                                    <PropertyView :items="activity.propertyItems" />
                                </div>
                            </template>
                        </ListCard>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.gutter {
    gap: 12px;
}
.activity-card {
    height: 100%;
    display: flex;
    justify-content: flex-start;
}
</style>
